import { create } from "zustand"
import { devtools } from "zustand/middleware"
import type {
  UpholsteryOptionsState,
  OptionValue,
  ProcessedOption,
} from "../components/types/store-types"
import { groupPatternOptions } from "../helpers/options-store-helpers"

/**
 * Helper function to process raw API options into simplified UI-ready format
 */
const processOptions = (options: OptionValue[]): ProcessedOption[] => {
  return options.map((opt) => ({
    id: opt.id,
    value: opt.optionValue,
    image: opt.optionImage?.responsiveImage?.src ?? "",
    label: opt.optionName,
    sku: opt.sku,
    compatibility: opt.compatibility,
  }))
}

/**
 * Upholstery Options Store
 *
 * Consolidates commonly-used option lists from the vehicle API response.
 * Provides both raw API data and processed UI-ready versions.
 *
 * Usage:
 * ```
 * const { insertColorOptions, processedInsertColors } = useUpholsteryOptionsStore()
 * ```
 */
export const useUpholsteryOptionsStore = create<UpholsteryOptionsState>()(
  devtools(
    (set) => ({
      // Initial state - empty arrays
      primaryColorOptions: [],
      insertColorOptions: [],
      stitchColorOptions: [],
      patternOptions: [],
      perforationOptions: [],
      toneOptions: [],
      kitOptions: [],
      colorInsertOptions: [],
      designPatternLocationOptions: [],
      processedPrimaryColors: [],
      processedInsertColors: [],
      processedStitchColors: [],
      processedPatterns: [],
      processedPerforations: [],
      processedTones: [],
      processedKits: [],
      processedColorInserts: [],
      processedGroupedPatterns: [],

      // Actions
      actions: {
        /**
         * Extract and cache option lists from the vehicle API response
         *
         * @param vehicle - The full vehicle object from the API
         */
        setOptionsFromVehicle: (vehicle) => {
          if (!vehicle || !vehicle.steps) {
            console.warn("Invalid vehicle data provided to options store")
            return
          }

          // ===== MATERIAL STEP OPTIONS =====
          const materialStep = vehicle.steps.find(
            (s: any) => s.key === "material"
          )

          // Primary Color
          const primaryColorSubstep = materialStep?.substeps?.find(
            (sub: any) => sub.key === "primary-color"
          )
          const primaryColorOptions =
            primaryColorSubstep?.category?.optionValues ?? []

          // Insert Color
          const insertSubstep = materialStep?.substeps?.find(
            (sub: any) => sub.key === "insert"
          )
          const insertColorOptions = insertSubstep?.category?.optionValues ?? []

          // Stitch Color
          const stitchSubstep = materialStep?.substeps?.find(
            (sub: any) => sub.key === "stitch"
          )
          const stitchColorOptions = stitchSubstep?.category?.optionValues ?? []

          // ===== PATTERN STEP OPTIONS =====
          const patternStep = vehicle.steps.find(
            (s: any) => s.key === "pattern"
          )

          // Pattern Options
          const patternSubstep = patternStep?.substeps?.find(
            (sub: any) => sub.key === "pattern"
          )
          const patternOptions = patternSubstep?.category?.optionValues ?? []

          // Design Pattern Location
          const designPatternLocationSubstep = patternStep?.substeps?.find(
            (sub: any) => sub.key === "design-pattern-location"
          )
          const designPatternLocationOptions =
            designPatternLocationSubstep?.category?.optionValues ?? []

          // ===== DESIGN STEP OPTIONS =====
          const designStep = vehicle.steps.find((s: any) => s.key === "design")

          // Perforation Options
          const perforationSubstep = designStep?.substeps?.find(
            (sub: any) => sub.key === "perforation"
          )
          const perforationOptions =
            perforationSubstep?.category?.optionValues ?? []

          // Tone Options
          const toneSubstep = designStep?.substeps?.find(
            (sub: any) => sub.key === "tone"
          )
          const toneOptions = toneSubstep?.category?.optionValues ?? []

          // Color Insert Options
          const colorInsertSubstep = designStep?.substeps?.find(
            (sub: any) => sub.key === "color-insert"
          )
          const colorInsertOptions =
            colorInsertSubstep?.category?.optionValues ?? []

          // ===== KIT STEP OPTIONS =====
          const kitStep = vehicle.steps.find((s: any) => s.key === "kit")
          const kitOptions = kitStep?.category?.optionValues ?? []

          // Process all options for UI consumption
          const processedPrimaryColors = processOptions(primaryColorOptions)
          const processedInsertColors = processOptions(insertColorOptions)
          const processedStitchColors = processOptions(stitchColorOptions)
          const processedPatterns = processOptions(patternOptions)
          const processedPerforations = processOptions(perforationOptions)
          const processedTones = processOptions(toneOptions)
          const processedKits = processOptions(kitOptions)
          const processedColorInserts = processOptions(colorInsertOptions)

          // Group patterns for UI
          const processedGroupedPatterns =
            groupPatternOptions(processedPatterns)

          // Update store
          set({
            // Raw options
            primaryColorOptions,
            insertColorOptions,
            stitchColorOptions,
            patternOptions,
            perforationOptions,
            toneOptions,
            kitOptions,
            colorInsertOptions,
            designPatternLocationOptions,
            // Processed options
            processedPrimaryColors,
            processedInsertColors,
            processedStitchColors,
            processedPatterns,
            processedPerforations,
            processedTones,
            processedKits,
            processedColorInserts,
            // Grouped patterns
            processedGroupedPatterns,
          })
        },

        /**
         * Clear all cached options (e.g., when switching vehicles)
         */
        clearOptions: () =>
          set({
            primaryColorOptions: [],
            insertColorOptions: [],
            stitchColorOptions: [],
            patternOptions: [],
            perforationOptions: [],
            toneOptions: [],
            kitOptions: [],
            colorInsertOptions: [],
            designPatternLocationOptions: [],
            processedPrimaryColors: [],
            processedInsertColors: [],
            processedStitchColors: [],
            processedPatterns: [],
            processedPerforations: [],
            processedTones: [],
            processedKits: [],
            processedColorInserts: [],
            processedGroupedPatterns: [],
          }),
      },
    }),
    { name: "UpholsteryOptionsStore" }
  )
)
